/* eslint-disable @typescript-eslint/no-floating-promises */
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router';
import { equipmentSchema } from '../../../../common/src/domain/drone/equipment/equipmentSchemas';
import { EquipmentStates } from '../../../../common/src/enums/EquipmentStates';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchGet } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { LocalizedDatePicker } from '../common/LocalizedDatePicker';
import { Content, EditPanel, EditPanelButtons, EditPanelCell, EditPanelRow, ISelectOption, Panel, PanelBody, PanelButton, Tray } from '../elements';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import {
    ValidatingEditPanelSaveButton,
    ValidatingFilterDropdown,
    ValidatingFilterDropdownWithUrl,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormInputDate,
    ValidatingFormInputText,
} from '../validatingElements';
import { TopBar } from './Common/TopBar';

function monthsEarlier(date: number) {
    const currentDate = moment(date);
    const earlierDate = currentDate.subtract(2, 'month');
    return earlierDate.toDate();
}

export function EquipmentAddEdit(props: { params: { id?: string } }) {
    const translate = useTranslate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const [equipmentTypes, setEquipmentTypes] = React.useState<ISelectOption[]>([]);
    const navigate = useNavigate();

    function handleSaveClicked(equipmentId: string, shouldGoToDocuments: boolean) {
        if (shouldGoToDocuments) {
            navigate(`/equipment/${equipmentId}/documents`);
        } else {
            navigate(`/equipment/${equipmentId}`);
        }
    }

    function handleCancelClicked(equipmentId?: string) {
        if (equipmentId) {
            navigate(`/equipment/${equipmentId}`);
        } else {
            navigate('/equipment');
        }
    }

    React.useEffect(() => {
        (async () => {
            const responseEquipmentTypes = await openApiFetchGet('/api/equipmentTypes', {});
            if (responseEquipmentTypes) {
                setEquipmentTypes(responseEquipmentTypes.map((item: { id: string; name: string }) => ({ text: item.name, value: item.id })));
            }
        })();
    }, []);

    return (
        <>
            <TopBar equipmentId={props.params.id} activeTab="addEdit" />
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody noPadding>
                            <ValidatingForm
                                schema={equipmentSchema}
                                createUrl={'/api/equipment/'}
                                updateUrl={`/api/equipment/${props.params.id}`}
                                itemUrl={`/api/equipment/${props.params.id}`}
                                id={props.params.id}
                                // eslint-disable-next-line @typescript-eslint/require-await
                                onSubmitted={async (id) => {
                                    if (props.params.id) {
                                        handleSaveClicked(props.params.id, false);
                                    } else {
                                        handleSaveClicked(id, true);
                                    }
                                }}
                            >
                                <EditPanel>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>
                                            <ValidatingFormInputText name="name" topLabel={translate('common.name')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    {isAdmin && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <ValidatingFilterDropdownWithUrl
                                                    name="clientCompanyId"
                                                    topLabel={translate('common.client')}
                                                    placeholder={translate('common.selectItem', translate('common.client'))}
                                                    url={'/api/clients'}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFilterDropdown
                                                name="status"
                                                topLabel="Status"
                                                placeholder={translate('common.selectItem', translate('common.status'))}
                                                selectOptions={[
                                                    ...[
                                                        { value: EquipmentStates.InUse, text: translate(`equipment.${EquipmentStates.InUse}`) },
                                                        {
                                                            value: EquipmentStates.InMaintenance,
                                                            text: translate(`equipment.${EquipmentStates.InMaintenance}`),
                                                        },
                                                        {
                                                            value: EquipmentStates.OutOfService,
                                                            text: translate(`equipment.${EquipmentStates.OutOfService}`),
                                                        },
                                                        {
                                                            value: EquipmentStates.UnderConstruction,
                                                            text: translate(`equipment.${EquipmentStates.UnderConstruction}`),
                                                        },
                                                    ],
                                                    ...(isAdmin
                                                        ? [
                                                              {
                                                                  value: EquipmentStates.Deleted,
                                                                  text: translate(`equipment.${EquipmentStates.Deleted}`),
                                                              },
                                                          ]
                                                        : []),
                                                ]}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFilterDropdown
                                                name="equipmentTypeId"
                                                topLabel={translate('equipment.types')}
                                                placeholder={translate('common.selectItem', translate('equipment.types'))}
                                                selectOptions={equipmentTypes}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="manufacturer" topLabel={translate('equipment.manufacturer')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="typeIndication" topLabel={translate('equipment.typeIndication')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="softwareVersion" topLabel={translate('equipment.softwareVersion')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="serialNumber" topLabel={translate('equipment.serialNumber')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputDate
                                                name="dateEndCertificateValidity"
                                                topLabel={translate('equipment.expirationDate')}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormContext.Consumer>
                                                {(state) => (
                                                    <>
                                                        <div className="ml5 mb5">{translate('equipment.reminderDate')} </div>
                                                        <label htmlFor="dateReminderRecertification">
                                                            <LocalizedDatePicker
                                                                id="dateReminderRecertification"
                                                                value={
                                                                    !state.item.dateReminderRecertification
                                                                        ? !state.item.dateEndCertificateValidity
                                                                            ? null
                                                                            : monthsEarlier(state.item.dateEndCertificateValidity)
                                                                        : new Date(state.item.dateReminderRecertification)
                                                                }
                                                                onChange={async (value) => state.onChange('dateReminderRecertification', value)}
                                                            />
                                                        </label>
                                                    </>
                                                )}
                                            </ValidatingFormContext.Consumer>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>
                                            <ValidatingFormInputText name="remark" topLabel={translate('common.remark')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                </EditPanel>
                                <EditPanelButtons footer>
                                    <PanelButton text={translate('common.cancel')} onClick={() => handleCancelClicked(props.params.id)} />
                                    <ValidatingEditPanelSaveButton />
                                </EditPanelButtons>
                            </ValidatingForm>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
